import { type CSSProperties, useEffect, useState } from 'react';

const FADE_PX = 24;

/*
    Fades the content out at the top or bottom edge when the user can scroll that way. A mask, not a
    white overlay, so the fade works on any background. StudentNetworkV2's useScrollFade does the same.
*/
export function useVerticalScrollFade(element: HTMLElement | null, enabled: boolean): CSSProperties {
    const [fade, setFade] = useState({ top: false, bottom: false });

    useEffect(() => {
        if (!element || !enabled) return undefined;

        const update = () => {
            const maxScroll = element.scrollHeight - element.clientHeight;
            setFade({ top: element.scrollTop > 1, bottom: maxScroll - element.scrollTop > 1 });
        };

        update();
        element.addEventListener('scroll', update, { passive: true });
        const observer = new ResizeObserver(update);
        observer.observe(element);
        // The content can grow without the body changing size, for example when more options render
        const mutationObserver = new MutationObserver(update);
        mutationObserver.observe(element, { childList: true, subtree: true });
        return () => {
            element.removeEventListener('scroll', update);
            observer.disconnect();
            mutationObserver.disconnect();
        };
    }, [element, enabled]);

    if (!enabled || (!fade.top && !fade.bottom)) return {};

    const mask = `linear-gradient(to bottom, transparent, black ${fade.top ? FADE_PX : 0}px, black calc(100% - ${
        fade.bottom ? FADE_PX : 0
    }px), transparent)`;
    return { maskImage: mask, WebkitMaskImage: mask };
}
